import React from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  IndianRupee, 
  BarChart3, 
  CreditCard, 
  AlertTriangle,
  Info,
  Calendar,
  Building,
  Hash,
  User,
  Banknote,
  Activity
} from 'lucide-react';
import { 
  BankStatementData, 
  formatCurrency, 
  formatNumber, 
  getRiskLevel 
} from '../utils/bankStatementUtils';
import {
  IncomeConsistencyAnalysis,
  CreditWorthinessSummaryCard,
  InvestmentAnalysis,
  RiskIndicators,
  LoanExpenseRatios,
  IrregularIncomeAnalysis,
  SuddenLargeWithdrawalAnalysis,
  HighValueTransactionAnalysis
} from './AdvancedAnalysisComponents';
import {
  MonthlyTrendsChart,
  BalanceTimelineChart,
  IncomeExpenseChart
} from './ChartComponents';

interface BankStatementResultsProps {
  data: BankStatementData;
}

// Analysis Card Component
const AnalysisCard: React.FC<{ title: string; children: React.ReactNode; className?: string }> = ({ 
  title, 
  children, 
  className = "" 
}) => (
  <div className={`bg-white rounded-xl shadow-lg border border-gray-200 ${className}`}>
    <div className="px-6 py-4 border-b border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
    </div>
    <div className="p-6">
      {children}
    </div>
  </div>
);

// Summary Card Component
const SummaryCard: React.FC<{
  title: string;
  value: string;
  icon: React.ReactNode;
  bgColor: string;
  textColor?: string;
  subtitle?: string;
}> = ({ title, value, icon, bgColor, textColor = "text-white", subtitle }) => (
  <div className={`${bgColor} ${textColor} p-6 rounded-xl shadow-lg`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm opacity-90 mb-1">{title}</p>
        <p className="text-2xl font-bold">{value}</p>
        {subtitle && <p className="text-sm opacity-75 mt-1">{subtitle}</p>}
      </div>
      <div className="w-12 h-12 flex items-center justify-center">
        {icon}
      </div>
    </div>
  </div>
);

// Risk Score Gauge Component
const RiskScoreGauge: React.FC<{ score: number }> = ({ score }) => {
  const riskInfo = getRiskLevel(score);
  const percentage = Math.min(score, 100);
  
  return (
    <div className="flex items-center space-x-4">
      <div className="relative w-20 h-20">
        <svg className="w-20 h-20 transform -rotate-90" viewBox="0 0 100 100">
          {/* Background circle */}
          <circle
            cx="50"
            cy="50"
            r="40"
            stroke="currentColor"
            strokeWidth="8"
            fill="none"
            className="text-gray-200"
          />
          {/* Progress circle */}
          <circle
            cx="50"
            cy="50"
            r="40"
            stroke="currentColor"
            strokeWidth="8"
            fill="none"
            strokeDasharray={`${percentage * 2.51} 251`}
            className={score <= 30 ? 'text-green-500' : score <= 50 ? 'text-yellow-500' : score <= 70 ? 'text-orange-500' : 'text-red-500'}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-lg font-bold text-gray-900">{score}</span>
        </div>
      </div>
      <div>
        <div className={`inline-flex px-3 py-1 rounded-full text-sm font-medium border ${riskInfo.color}`}>
          {riskInfo.level}
        </div>
        <p className="text-sm text-gray-600 mt-1 max-w-xs">{riskInfo.description}</p>
      </div>
    </div>
  );
};



const BankStatementResults: React.FC<BankStatementResultsProps> = ({ data }) => {
  const primaryAccount = data.accountHolderDetails[0];
  
  if (!primaryAccount) {
    return (
      <div className="text-center py-8">
        <AlertTriangle className="mx-auto h-12 w-12 text-yellow-500 mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Account Data Found</h3>
        <p className="text-gray-600">Unable to process bank statement analysis. Please try again.</p>
      </div>
    );
  }

  // Check if we have transaction-specific data to determine data sources
  const hasTransactionData = data.bankTransactions && data.bankTransactions.length > 0;
  const hasAdvancedTransactionData = (data.top10Credits && data.top10Credits.length > 0) || 
                                     (data.top10Debits && data.top10Debits.length > 0);

  return (
    <div className="space-y-8 w-full">
      {/* Data Source Indicator */}
      {(hasTransactionData || hasAdvancedTransactionData) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Info className="w-5 h-5 text-blue-600" />
            <div>
              <h4 className="text-sm font-medium text-blue-900">Enhanced Data Analysis</h4>
              <p className="text-sm text-blue-700">
                This analysis combines data from multiple sources for comprehensive insights including 
                detailed transaction analysis and advanced financial metrics.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <SummaryCard
          title="Total Credits"
          value={formatCurrency(data.totalCredits)}
          icon={<TrendingUp className="w-6 h-6" />}
          bgColor="bg-gradient-to-r from-green-500 to-green-600"
          subtitle={`${formatNumber(data.top10Credits.length)} top transactions`}
        />
        <SummaryCard
          title="Total Debits"
          value={formatCurrency(data.totalDebits)}
          icon={<TrendingDown className="w-6 h-6" />}
          bgColor="bg-gradient-to-r from-red-500 to-red-600"
          subtitle={`${formatNumber(data.top10Debits.length)} transactions`}
        />
        <SummaryCard
          title="Average Balance"
          value={formatCurrency(data.averageBalance)}
          icon={<IndianRupee className="w-6 h-6" />}
          bgColor="bg-gradient-to-r from-blue-500 to-blue-600"
          subtitle={data.period}
        />
        <SummaryCard
          title="Net Cash Flow"
          value={formatCurrency(data.netCashFlow)}
          icon={<BarChart3 className="w-6 h-6" />}
          bgColor={data.netCashFlow >= 0 ? "bg-gradient-to-r from-emerald-500 to-emerald-600" : "bg-gradient-to-r from-orange-500 to-orange-600"}
          subtitle={data.expensesToIncomeRatio}
        />
      </div>

      {/* Risk Assessment and Credit Score */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <AnalysisCard title="Risk Assessment">
          <RiskScoreGauge score={data.riskScore} />
          <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Bounce Count:</span>
              <span className="font-semibold text-gray-900 ml-2">{data.bounceCount}</span>
            </div>
            <div>
              <span className="text-gray-600">Overdraft Frequency:</span>
              <span className="font-semibold text-gray-900 ml-2">{data.overdraftFrequency}</span>
            </div>
            <div>
              <span className="text-gray-600">EMI Count:</span>
              <span className="font-semibold text-gray-900 ml-2">{data.emiCount}</span>
            </div>
            <div>
              <span className="text-gray-600">Total Transactions:</span>
              <span className="font-semibold text-gray-900 ml-2">{formatNumber(data.transactionCount)}</span>
            </div>
          </div>
        </AnalysisCard>

        <AnalysisCard title="Income and Expense Analysis">
          {/* <CreditScoreDisplay score={data.creditScore} /> */}
          <div className="mt-4 pt-4">
            <div className="grid grid-cols-1 gap-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Monthly Income:</span>
                <span className="font-semibold">{formatCurrency(data.averageMonthlyIncome)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Monthly Expense:</span>
                <span className="font-semibold">{formatCurrency(data.averageMonthlyExpense)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Savings Rate:</span>
                <span className="font-semibold">
                  {data.averageMonthlyIncome > 0 
                    ? `${(((data.averageMonthlyIncome - data.averageMonthlyExpense) / data.averageMonthlyIncome) * 100).toFixed(1)}%`
                    : '0%'
                  }
                </span>
              </div>
            </div>
          </div>
        </AnalysisCard>
      </div>

      {/* Account Information */}
      <AnalysisCard title="Account Information">
        {data.accountHolderDetails.map((account, index) => (
          <div key={index} className="p-4 rounded-lg bg-gray-50 border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="flex items-center space-x-2">
                <User className="w-4 h-4 text-gray-500" />
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Account Name</p>
                  <p className="font-semibold text-gray-900">{account.account_name}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Hash className="w-4 h-4 text-gray-500" />
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Account Number</p>
                  <p className="font-semibold text-gray-900">{account.account_number}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Building className="w-4 h-4 text-gray-500" />
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Bank Name</p>
                  <p className="font-semibold text-gray-900">{account.bank_name}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Period</p>
                  <p className="font-semibold text-gray-900">{account.Period}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Banknote className="w-4 h-4 text-gray-500" />
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Opening Balance</p>
                  <p className="font-semibold text-gray-900">{formatCurrency(account.OpeningBalance)}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <CreditCard className="w-4 h-4 text-gray-500" />
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Closing Balance</p>
                  <p className="font-semibold text-gray-900">{formatCurrency(account.ClosingBalance)}</p>
                </div>
              </div>
            </div>
            {account.OD_CC_Limit > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-300">
                <div className="flex items-center space-x-2">
                  <Info className="w-4 h-4 text-blue-500" />
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Overdraft/Credit Limit</p>
                    <p className="font-semibold text-blue-900">{formatCurrency(account.OD_CC_Limit)}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </AnalysisCard>

      {/* Top Credits */}
      <AnalysisCard title="Top 10 Credits">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.top10Credits.slice(0, 10).map((credit, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {credit.description}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                    {credit.L3_Labels}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                    {new Date(credit.value_date).toLocaleDateString('en-IN')}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-green-600 text-right">
                    {formatCurrency(credit.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AnalysisCard>

      {/* Top Debits */}
      <AnalysisCard title="Top 10 Debits">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.top10Debits.slice(0, 10).map((debit, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {debit.description}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                    {debit.L3_Labels}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                    {new Date(debit.value_date).toLocaleDateString('en-IN')}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-red-600 text-right">
                    {formatCurrency(debit.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AnalysisCard>

      {/* Visual Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Trends Chart */}
        <MonthlyTrendsChart data={data.monthlyAnalysis} />
        
        {/* Income vs Expense Chart */}
        <IncomeExpenseChart data={data.monthlyIncomeExpense} />
      </div>

      {/* Balance Timeline */}
      <BalanceTimelineChart data={data.dailyEODBalance} />

      {/* Monthly Analysis Table */}
      {data.monthlyAnalysis.length > 0 && (
        <AnalysisCard title="Detailed Monthly Analysis">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Month
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Credits
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Debits
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Transaction Count
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    EOM Balance
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Net Flow
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.monthlyAnalysis.map((month, index) => {
                  const credits = parseFloat(month.TotalCredits.replace(/[₹,]/g, '') || '0');
                  const debits = parseFloat(month.TotalDebits.replace(/[₹,]/g, '') || '0');
                  const netFlow = credits - debits;
                  const totalTransactions = parseInt(month.NoOfCredits) + parseInt(month.NoOfDebits);
                  
                  return (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {month.Month}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-green-600 text-right font-semibold">
                        {formatCurrency(credits)}
                        <div className="text-xs text-gray-500">({month.NoOfCredits} txns)</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-red-600 text-right font-semibold">
                        {formatCurrency(debits)}
                        <div className="text-xs text-gray-500">({month.NoOfDebits} txns)</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {totalTransactions}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold">
                        {formatCurrency(parseFloat(month.EOMBalance.replace(/[₹,]/g, '') || '0'))}
                      </td>
                      <td className={`px-4 py-4 whitespace-nowrap text-sm text-right font-semibold ${
                        netFlow >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {formatCurrency(netFlow)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </AnalysisCard>
      )}

      {/* Advanced Analysis Sections */}
      
      {/* Income Consistency Analysis */}
      <IncomeConsistencyAnalysis data={data.incomeConsistency} />
      
      {/* Credit Worthiness Summary */}
      <CreditWorthinessSummaryCard data={data.creditWorthinessSummary} />
      
      {/* Investment Analysis */}
      <InvestmentAnalysis 
        transactions={data.investmentTransactions} 
        ratio={data.investmentRatioRepeater} 
      />
      
      {/* Risk Indicators */}
      <RiskIndicators 
        exceptionalTransactions={data.exceptionalTransactions}
        monthlyOverdraft={data.monthlyOverdraft}
        highValueTransactions={data.highValueTransactions}
      />
      
      {/* Loan and Expense Ratios */}
      <LoanExpenseRatios 
        loanRatio={data.loanPaymentRatio}
        expenseRatio={data.expenseRatio}
      />
      
      {/* Enhanced Risk Analysis Sections */}
      
      {/* Irregular Income Analysis */}
      <IrregularIncomeAnalysis data={data.irregularIncome} />
      
      {/* Sudden Large Withdrawal Analysis */}
      <SuddenLargeWithdrawalAnalysis data={data.suddenLargeWithdrawal} />
      
      {/* Enhanced High Value Transaction Analysis */}
      <HighValueTransactionAnalysis data={data.highValueTransactions} />

      {/* Enhanced Financial Metrics */}
      <AnalysisCard title="Enhanced Financial Metrics">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600">Savings Rate</p>
                <p className="text-2xl font-bold text-blue-900">{data.savingsRatePercentage.toFixed(1)}%</p>
              </div>
              <TrendingUp className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          
          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600">Income Consistency</p>
                <p className="text-2xl font-bold text-green-900">{data.incomeConsistencyScore.toFixed(0)}%</p>
              </div>
              <BarChart3 className="w-6 h-6 text-green-600" />
            </div>
          </div>
          
          <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600">Investment Ratio</p>
                <p className="text-2xl font-bold text-purple-900">{data.investmentRatio.toFixed(2)}%</p>
              </div>
              <Activity className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          
          <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-600">Loan-to-Income</p>
                <p className="text-2xl font-bold text-orange-900">{data.loanToIncomeRatio.toFixed(1)}%</p>
              </div>
              <CreditCard className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>
      </AnalysisCard>

      {/* Fund Remittances Analysis */}
      {(data.topFundRemittances.length > 0 || data.topFundCredit.length > 0 || data.topLowestFundCredit.length > 0 || data.below5Remittance.length > 0) && (
        <AnalysisCard title="Fund Transfer Analysis">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Fund Remittances */}
            {data.topFundRemittances.length > 0 && (
              <div>
                <h4 className="text-md font-semibold mb-3 text-red-600">Top Fund Remittances (Outgoing)</h4>
                <div className="space-y-2">
                  {data.topFundRemittances.slice(0, 5).map((remittance, index) => (
                    <div key={index} className="p-3 bg-red-50 rounded-lg border border-red-200">
                      <div className="flex justify-between items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900 font-medium truncate" title={remittance.Description}>
                            {remittance.Description}
                          </p>
                          <p className="text-xs text-gray-600 truncate">
                            {new Date(remittance.TransactionDate).toLocaleDateString('en-IN')} • {remittance.TransactionMonth}
                          </p>
                        </div>
                        <span className="text-sm font-bold text-red-600 flex-shrink-0">
                          {formatCurrency(remittance.Amount)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Top Fund Credits */}
            {data.topFundCredit.length > 0 && (
              <div>
                <h4 className="text-md font-semibold mb-3 text-green-600">Top Fund Credits (Incoming)</h4>
                <div className="space-y-2">
                  {data.topFundCredit.slice(0, 5).map((credit, index) => (
                    <div key={index} className="p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex justify-between items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900 font-medium truncate" title={credit.Description}>
                            {credit.Description}
                          </p>
                          <p className="text-xs text-gray-600 truncate">
                            {new Date(credit.TransactionDate).toLocaleDateString('en-IN')} • {credit.TransactionMonth}
                          </p>
                        </div>
                        <span className="text-sm font-bold text-green-600 flex-shrink-0">
                          {formatCurrency(credit.Amount)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Bottom Row - Small Amount Transfers */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            {/* Top Lowest Fund Credits */}
            {data.topLowestFundCredit.length > 0 && (
              <div>
                <h4 className="text-md font-semibold mb-3 text-blue-600">Lowest Fund Credits</h4>
                <div className="space-y-2">
                  {data.topLowestFundCredit.slice(0, 5).map((credit, index) => (
                    <div key={index} className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex justify-between items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900 font-medium truncate" title={credit.Description}>
                            {credit.Description}
                          </p>
                          <p className="text-xs text-gray-600 truncate">
                            {new Date(credit.TransactionDate).toLocaleDateString('en-IN')} • {credit.TransactionMonth}
                          </p>
                        </div>
                        <span className="text-sm font-bold text-blue-600 flex-shrink-0">
                          {formatCurrency(credit.Amount)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Below ₹5 Remittances */}
            {data.below5Remittance.length > 0 && (
              <div>
                <h4 className="text-md font-semibold mb-3 text-purple-600">Micro Transactions (&lt; ₹5)</h4>
                <div className="space-y-2">
                  {data.below5Remittance.slice(0, 5).map((micro, index) => (
                    <div key={index} className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                      <div className="flex justify-between items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900 font-medium truncate" title={micro.Description}>
                            {micro.Description}
                          </p>
                          <p className="text-xs text-gray-600 truncate">
                            {new Date(micro.TransactionDate).toLocaleDateString('en-IN')} • {micro.TransactionMonth}
                          </p>
                        </div>
                        <span className="text-sm font-bold text-purple-600 flex-shrink-0">
                          {formatCurrency(micro.Amount)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </AnalysisCard>
      )}

      {/* Summary of Loan Transactions */}
      {data.summaryOfLoanTransactions.length > 0 && (
        <AnalysisCard title="Summary of Loan Transactions">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Period
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Debits
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Credits
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Net Flow
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.summaryOfLoanTransactions.map((loan, index) => {
                  const netFlow = loan.TotalCredits - loan.TotalDebits;
                  return (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {loan.TransactionDate}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-red-600 text-right font-semibold">
                        {formatCurrency(loan.TotalDebits)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-green-600 text-right font-semibold">
                        {formatCurrency(loan.TotalCredits)}
                      </td>
                      <td className={`px-4 py-4 whitespace-nowrap text-sm text-right font-semibold ${
                        netFlow >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {formatCurrency(netFlow)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </AnalysisCard>
      )}

      {/* Details of Loan Summary */}
      {data.detailsOfLoanSummary.length > 0 && (
        <AnalysisCard title="Details of Loan Summary">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Debits
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Credits
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.detailsOfLoanSummary.map((detail, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {new Date(detail.value_date).toLocaleDateString('en-IN')}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                      {detail.L3_Labels}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-red-600 text-right font-semibold">
                      {formatCurrency(detail.TotalDebits)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-green-600 text-right font-semibold">
                      {formatCurrency(detail.TotalCredits)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AnalysisCard>
      )}

      {/* Loan Transaction Summary */}
      {data.loanTransactionSummary.length > 0 && (
        <AnalysisCard title="Loan Transaction Summary">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Period
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Debits
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Credits
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Net Flow
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.loanTransactionSummary.map((loan, index) => {
                  const netFlow = loan.TotalCredits - loan.TotalDebits;
                  return (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {loan.TransactionDate}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-red-600 text-right font-semibold">
                        {formatCurrency(loan.TotalDebits)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-green-600 text-right font-semibold">
                        {formatCurrency(loan.TotalCredits)}
                      </td>
                      <td className={`px-4 py-4 whitespace-nowrap text-sm text-right font-semibold ${
                        netFlow >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {formatCurrency(netFlow)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </AnalysisCard>
      )}

      {/* EMI Payment Summary */}
      {data.emiPaymentCount.length > 0 && (
        <AnalysisCard title="EMI Payment Summary">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {data.emiPaymentCount.map((emi, index) => (
              <div key={index} className="p-6 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold">{emi.EMIPaymentCount}</div>
                    <div className="text-sm opacity-90">EMI Payments</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold">{formatCurrency(emi.TotalEMIAmount)}</div>
                    <div className="text-sm opacity-90">Total Amount</div>
                  </div>
                </div>
                <div className="mt-4 text-center">
                  <div className="text-sm opacity-90">Average EMI</div>
                  <div className="text-xl font-bold">
                    {formatCurrency(emi.TotalEMIAmount / Math.max(emi.EMIPaymentCount, 1))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </AnalysisCard>
      )}

      {/* Basic Income Expense Analysis */}
      {data.incomeExpense.length > 0 && (
        <AnalysisCard title="Basic Income & Expense Analysis">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Month
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Monthly Income
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Monthly Expense
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Current Balance
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Net Savings
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.incomeExpense.map((item, index) => {
                  const netSavings = item.MonthlyIncome - item.MonthlyExpense;
                  return (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.Month}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-green-600 text-right font-semibold">
                        {formatCurrency(item.MonthlyIncome)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-red-600 text-right font-semibold">
                        {formatCurrency(item.MonthlyExpense)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-blue-600 text-right font-semibold">
                        {formatCurrency(item.CurrentBalance)}
                      </td>
                      <td className={`px-4 py-4 whitespace-nowrap text-sm text-right font-semibold ${
                        netSavings >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {formatCurrency(netSavings)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </AnalysisCard>
      )}

      {/* Daily Average Balance Analysis */}
      {data.dailyAverageBalance.length > 0 && (
        <AnalysisCard title="Daily Average Balance Analysis">
          {data.dailyAverageBalance.map((balance, index) => (
            <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-900">
                  {formatCurrency(balance["Daily Average EOD Balance"])}
                </div>
                <div className="text-sm text-blue-600">Daily Average EOD Balance</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-900">
                  {formatCurrency(balance["Average Monthly Business Credit"])}
                </div>
                <div className="text-sm text-green-600">Avg Monthly Business Credit</div>
              </div>
              {/* <div className="text-center p-4 bg-purple-50 rounded-lg">
                <div className="text-lg font-bold text-purple-900">{balance.Description}</div>
                <div className="text-sm text-purple-600">Analysis Type</div>
              </div> */}
            </div>
          ))}
        </AnalysisCard>
      )}

      {/* Monthly Average Balance Analysis */}
      {data.monthlyAverageBalance.length > 0 && (
        <AnalysisCard title="Monthly Average Balance Analysis">
          {data.monthlyAverageBalance.map((balance, index) => (
            <div key={index} className="text-center p-6 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white rounded-xl">
              <div className="text-3xl font-bold">
                {formatCurrency(balance["Average Balance"])}
              </div>
              <div className="text-lg opacity-90 mt-2">{balance.Description}</div>
              <div className="text-sm opacity-75 mt-1">Monthly Average Balance</div>
            </div>
          ))}
        </AnalysisCard>
      )}

      {/* All KPIs */}
      {data.allKPIs.length > 0 && (
        <AnalysisCard title="All Key Performance Indicators">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.allKPIs.slice(0, 12).map((kpi, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">{kpi.L3_Labels || `KPI ${index + 1}`}</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {kpi.transaction_type === 'CREDIT' 
                        ? formatCurrency(kpi.credits)
                        : formatCurrency(kpi.debits)
                      }
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(kpi.value_date).toLocaleDateString('en-IN')}
                    </p>
                  </div>
                  <Activity className="w-5 h-5 text-gray-400" />
                </div>
              </div>
            ))}
          </div>
        </AnalysisCard>
      )}

      {/* Savings Rate Analysis */}
      {data.savingsRate.length > 0 && (
        <AnalysisCard title="Savings Rate Analysis">
          <div className="text-center p-6 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl">
            <div className="text-lg opacity-90">Savings Rate Data Available</div>
            <div className="text-sm opacity-75 mt-2">
              {data.savingsRate.length} savings rate record(s) found
            </div>
          </div>
        </AnalysisCard>
      )}

      {/* Salary Consistency Analysis */}
      {data.salaryConsistency.length > 0 && (
        <AnalysisCard title="Salary Consistency Analysis">
          <div className="text-center p-6 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white rounded-xl">
            <div className="text-lg opacity-90">Salary Consistency Data Available</div>
            <div className="text-sm opacity-75 mt-2">
              {data.salaryConsistency.length} salary consistency record(s) found
            </div>
          </div>
        </AnalysisCard>
      )}
    </div>
  );
};

export default BankStatementResults;
